import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

export const TOUR_KEY = "logbuch-tour-seen";

interface TourStep {
  /** Where the user should be while reading this step. Empty = stay on current page (intro/outro). */
  route?: string;
  /** data-tour="..." attribute on the element to highlight on that route. */
  anchor?: string;
  title: string;
  body: string;
}

const STEPS: TourStep[] = [
  {
    title: "Logbuch — kurze Tour",
    body:
      "5 Minuten. Ich zeige dir nacheinander, wo dein Großziel, Budget, Bank-Anbindung und Regeln leben — direkt im echten App-Layout.",
  },
  {
    route: "/goals",
    anchor: "grossziel",
    title: "Großziel",
    body:
      'Dein langfristiger Anker (z. B. „Bootsleben"). Hier siehst du den Gesamtfortschritt und kannst Unter-Posten hinzufügen.',
  },
  {
    route: "/budget",
    anchor: "budget-overview",
    title: "Budget",
    body:
      "Pro Monat: was geplant ist vs. was schon ausgegeben wurde. Jede Kategorie ist ein Briefumschlag. Tippe einen Wert an, um ihn zu ändern.",
  },
  {
    route: "/einstellungen#konten",
    anchor: "konten-banken",
    title: "Bank-Anbindung",
    body:
      "Konto + Bank wohnen zusammen. Verbindest du deine Bank, holt Logbuch deine Umsätze täglich automatisch.",
  },
  {
    route: "/zuordnung",
    title: "Zuordnung",
    body:
      "Logbuch lernt, welche Buchung in welchen Budgetposten gehört. Auto-Scan schlägt Regeln vor — du bestätigst, was passt.",
  },
  {
    route: "/sparziele",
    anchor: "sparziel-card",
    title: "Kleine Sparziele",
    body:
      "Kurzfristige Ziele wie Urlaub oder Anschaffungen — getrennt vom Großziel, mit eigener Zeitachse.",
  },
  {
    route: "/einstellungen#einladungen",
    anchor: "einladungen",
    title: "Mitglieder einladen",
    body:
      'Tippe „+ Neue Einladung", kopiere den Link und schicke ihn deinem Partner. Er meldet sich an, folgt dem Link, und teilt automatisch deinen Haushalt.',
  },
  {
    route: "/einstellungen#feedback",
    anchor: "feedback",
    title: "Feedback & Probleme",
    body:
      "Wenn etwas nicht klappt oder du was vermisst — schreib's uns hier. Wir lesen alles.",
  },
  {
    title: "Fertig",
    body:
      'Du kannst die Tour jederzeit unter /Einstellungen → Ansicht → „Tour neu starten" wieder aufrufen.',
  },
];

const TOUR_STEP_KEY = "logbuch-tour-step";

export default function Tour({ onClose }: { onClose: () => void }) {
  // Persist the step index in sessionStorage so the dialog survives any
  // accidental re-mount (e.g. when navigation triggers a redirect via
  // useAuthGate) — previously the user could land on /goals and find the
  // tour gone because some state path reset the step.
  const [i, setI] = useState<number>(() => {
    try {
      const v = sessionStorage.getItem(TOUR_STEP_KEY);
      const n = v == null ? 0 : Number.parseInt(v, 10);
      return Number.isFinite(n) && n >= 0 && n < STEPS.length ? n : 0;
    } catch {
      return 0;
    }
  });
  useEffect(() => {
    try {
      sessionStorage.setItem(TOUR_STEP_KEY, String(i));
    } catch {
      /* sessionStorage unavailable — fine, just no persistence */
    }
  }, [i]);
  const navigate = useNavigate();
  const loc = useLocation();
  const step = STEPS[i];
  const last = i === STEPS.length - 1;
  const highlightedRef = useRef<HTMLElement | null>(null);

  // Navigate to the step's route. Use setTimeout to ensure router has settled
  // before we look for the anchor element.
  useEffect(() => {
    if (step.route) {
      const [path, hash] = step.route.split("#");
      const target = path + (hash ? `#${hash}` : "");
      if (loc.pathname + loc.hash !== target) {
        navigate(step.route);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);

  // After render, find + highlight the anchor element.
  useEffect(() => {
    // Cleanup previous highlight
    if (highlightedRef.current) {
      highlightedRef.current.classList.remove(
        "ring-2",
        "ring-brand-400",
        "ring-offset-2",
        "ring-offset-ink-950",
        "rounded-lg",
        "transition",
      );
      highlightedRef.current = null;
    }
    if (!step.anchor) return;
    // Zielseiten laden ihre Sektionen async (z. B. /einstellungen fetcht erst
    // Konten/Einladungen) — deshalb pollen statt einmaligem Timeout, sonst
    // wird der Anker verpasst und der Schritt bleibt ohne Highlight.
    let tries = 0;
    const t = setInterval(() => {
      const el = document.querySelector<HTMLElement>(`[data-tour="${step.anchor}"]`);
      if (!el) {
        if (++tries >= 24) clearInterval(t); // ~6s, dann aufgeben
        return;
      }
      clearInterval(t);
      el.classList.add(
        "ring-2",
        "ring-brand-400",
        "ring-offset-2",
        "ring-offset-ink-950",
        "rounded-lg",
        "transition",
      );
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      highlightedRef.current = el;
    }, 250);
    return () => clearInterval(t);
  }, [i, step.anchor, loc.pathname, loc.hash]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") finish();
      else if (e.key === "ArrowRight") setI((n) => Math.min(STEPS.length - 1, n + 1));
      else if (e.key === "ArrowLeft") setI((n) => Math.max(0, n - 1));
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (highlightedRef.current) {
        highlightedRef.current.classList.remove(
          "ring-2",
          "ring-brand-400",
          "ring-offset-2",
          "ring-offset-ink-950",
          "rounded-lg",
          "transition",
        );
      }
    };
  }, []);

  function finish() {
    window.localStorage.setItem(TOUR_KEY, "1");
    try {
      sessionStorage.removeItem(TOUR_STEP_KEY);
    } catch {
      /* ignore */
    }
    onClose();
  }

  return (
    <>
      {/* Subtle dim — does NOT block clicks. User can scroll/interact with
          the page underneath if they want, to ground the explanation. */}
      <div className="pointer-events-none fixed inset-0 z-[60] bg-black/30" />

      {/* Bottom-anchored card on mobile, bottom-right on desktop. z-[70] so
          it stays above anything else that might paint over it (setup
          wizard, modals, mobile drawers). */}
      <div
        className="fixed inset-x-3 z-[70] md:inset-x-auto md:right-6 md:max-w-sm md:w-[22rem] card p-4 shadow-2xl border-brand-500/40"
        style={{
          bottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))",
        }}
        role="dialog"
        aria-modal="false"
      >
        <div className="flex items-center gap-1 mb-3">
          {STEPS.map((_, j) => (
            <span
              key={j}
              className={
                "h-1 flex-1 rounded-full transition " +
                (j < i ? "bg-brand-500" : j === i ? "bg-brand-400" : "bg-ink-800")
              }
            />
          ))}
        </div>
        <h2 className="text-sm font-semibold text-ink-100 mb-1">{step.title}</h2>
        <p className="text-xs text-ink-300 leading-relaxed">{step.body}</p>
        <div className="flex items-center gap-2 mt-3">
          <button
            type="button"
            onClick={() => setI((n) => Math.max(0, n - 1))}
            disabled={i === 0}
            className="btn-ghost text-xs disabled:opacity-40"
          >
            Zurück
          </button>
          <span className="text-[10px] text-ink-500 mx-auto num">
            {i + 1} / {STEPS.length}
          </span>
          {last ? (
            <button onClick={finish} className="btn-primary text-xs">
              Fertig
            </button>
          ) : (
            <button
              onClick={() => setI((n) => Math.min(STEPS.length - 1, n + 1))}
              className="btn-primary text-xs"
            >
              Weiter →
            </button>
          )}
        </div>
        <button
          onClick={finish}
          className="absolute top-2 right-2 text-ink-500 hover:text-ink-200 text-xs"
          aria-label="Tour schließen"
        >
          ✕
        </button>
      </div>
    </>
  );
}
